import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { formatPrice } from "../../utils/priceUtils";
import { formatVolume } from "../../utils/numberFormatting";
import { useDepthBarCanvas } from "./hooks/useDepthBarCanvas";

const BID_BAR = "rgba(50,80,220,0.35)";
const ASK_BAR = "rgba(200,40,50,0.35)";

const BookByPriceTraditionalView = ({ symbol, bids = [], asks = [] }) => {
  const tableRef = useRef(null);
  const depthCanvasRef = useRef(null);

  const maxQty = useMemo(() => {
    let m = 0;
    for (const b of bids) if (b?.quantity > m) m = b.quantity;
    for (const a of asks) if (a?.quantity > m) m = a.quantity;
    return m;
  }, [bids, asks]);

  const rowCount = Math.max(bids.length, asks.length);

  // Same {bid, ask} row shape the canvas hook expects (SuperDOM passes its
  // ladder); memoized so the hook's effect only fires when data changes.
  const rows = useMemo(
    () =>
      Array.from({ length: rowCount }, (_, i) => ({
        bid: bids[i],
        ask: asks[i],
      })),
    [bids, asks, rowCount],
  );

  // Depth bars on a canvas behind the table instead of a per-<tr> gradient.
  useDepthBarCanvas({
    canvasRef: depthCanvasRef,
    tableRef,
    rows,
    maxQty,
    bidColor: BID_BAR,
    askColor: ASK_BAR,
  });

  return (
    <>
      <canvas
        ref={depthCanvasRef}
        className="bbp-depth-canvas"
        aria-hidden="true"
      />
      <table
        ref={tableRef}
        className="bbp-table"
        data-evo-id="bookbypricepanel-table-traditional"
      >
        <colgroup>
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th title="Compradores">#</th>
            <th title="Quantidade compradora">Qtd</th>
            <th title="Preço de compra">Compra</th>
            <th title="Preço de venda">Venda</th>
            <th title="Quantidade vendedora">Qtd</th>
            <th title="Vendedores">#</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rowCount }, (_, i) => {
            const bid = bids[i];
            const ask = asks[i];
            return (
              <tr key={i}>
                {bid ? (
                  <td className="bbp-cell--bid">{formatVolume(bid.orders)}</td>
                ) : (
                  <td className="bbp-cell--dash">—</td>
                )}
                {bid ? (
                  <td className="bbp-cell--bid">
                    {formatVolume(bid.quantity)}
                  </td>
                ) : (
                  <td className="bbp-cell--dash">—</td>
                )}
                {bid ? (
                  <td className="bbp-cell--bid bbp-cell--bid-price">
                    {formatPrice(symbol, bid.price)}
                  </td>
                ) : (
                  <td className="bbp-cell--dash">—</td>
                )}
                {ask ? (
                  <td className="bbp-cell--ask bbp-cell--ask-price">
                    {formatPrice(symbol, ask.price)}
                  </td>
                ) : (
                  <td className="bbp-cell--dash">—</td>
                )}
                {ask ? (
                  <td className="bbp-cell--ask">
                    {formatVolume(ask.quantity)}
                  </td>
                ) : (
                  <td className="bbp-cell--dash">—</td>
                )}
                {ask ? (
                  <td className="bbp-cell--ask">{formatVolume(ask.orders)}</td>
                ) : (
                  <td className="bbp-cell--dash">—</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export default React.memo(BookByPriceTraditionalView);

const bookLevelShape = PropTypes.shape({
  price: PropTypes.number,
  quantity: PropTypes.number,
  orders: PropTypes.number,
});

BookByPriceTraditionalView.propTypes = {
  symbol: PropTypes.string,
  bids: PropTypes.arrayOf(bookLevelShape),
  asks: PropTypes.arrayOf(bookLevelShape),
};
